import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, IconButton, Tooltip, Typography, Button } from '@mui/material';
import {
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as CenterIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import Draggable from 'react-draggable';
import StepNode from './StepNode';
import { useDrop } from 'react-dnd';
import ConnectionLine from './ConnectionLine';
import { api } from '../services/authService';

const JourneyCanvas = ({
  steps,
  connections,
  selectedStep,
  onSelectStep,
  onAddStep,
  onUpdateStep,
  onAddConnection,
  onEditConnection,
  onDeleteConnection,
  onDeleteStep,
  isConnecting,
  connectionSource,
  onStartConnection,
  onCompleteConnection,
  journey,
}) => {
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDraggingCanvas, setIsDraggingCanvas] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isCreatingConnection, setIsCreatingConnection] = useState(false);
  const [connectionStart, setConnectionStart] = useState(null);
  const [connectionEnd, setConnectionEnd] = useState(null);
  const canvasRef = useRef(null);
  const [canvasBounds, setCanvasBounds] = useState({ width: 0, height: 0 });
  const [selectedConnection, setSelectedConnection] = useState(null);
  const [error, setError] = useState(null);

  // Log initial props
  console.log('JourneyCanvas Props:', {
    stepsCount: steps.length,
    connectionsCount: connections.length,
    selectedStepId: selectedStep?.id,
    isConnecting,
    connectionSource,
    journey,
    journeyFunnelId: journey?.funnel
  });

  // Update canvas bounds when the component mounts and when window resizes
  useEffect(() => {
    const updateBounds = () => {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setCanvasBounds({
          width: rect.width,
          height: rect.height,
        });
      }
    };

    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, []);

  const handleZoomIn = () => {
    setZoom((prev) => Math.min(prev + 0.1, 2));
  };

  const handleZoomOut = () => {
    setZoom((prev) => Math.max(prev - 0.1, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  const handleCanvasMouseDown = (e) => {
    // Only start dragging if it's a middle mouse button or space key is held
    if (e.button === 1 || (e.button === 0 && e.altKey)) {
      setIsDraggingCanvas(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (isDraggingCanvas) {
      const dx = e.clientX - dragStart.x;
      const dy = e.clientY - dragStart.y;
      setPan((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
      setDragStart({ x: e.clientX, y: e.clientY });
    }

    // Update connection end position when creating a connection
    if (isCreatingConnection) {
      const canvasRect = canvasRef.current?.getBoundingClientRect();
      if (!canvasRect) return;

      // Calculate position relative to canvas, accounting for zoom and pan
      const x = (e.clientX - canvasRect.left - pan.x) / zoom;
      const y = (e.clientY - canvasRect.top - pan.y) / zoom;

      setConnectionEnd({ x, y });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsDraggingCanvas(false);

    // Cancel connection creation if mouse is released without connecting to a node
    if (isCreatingConnection) {
      setIsCreatingConnection(false);
      setConnectionStart(null);
      setConnectionEnd(null);
    }
  };

  const handleCanvasClick = (e) => {
    // Only deselect if clicking directly on the canvas (not on a node)
    if (e.target === canvasRef.current) {
      onSelectStep(null);

      // Cancel any in-progress connection
      if (isCreatingConnection) {
        setIsCreatingConnection(false);
        setConnectionStart(null);
        setConnectionEnd(null);
      }
    }
  };

  const handleStartConnection = useCallback((stepId, position) => {
    setIsCreatingConnection(true);
    setConnectionStart({ stepId, position });
    setConnectionEnd(position);
  }, []);

  const handleCompleteConnection = useCallback(
    (targetStepId) => {
      if (connectionStart && connectionStart.stepId !== targetStepId) {
        const sourceStep = steps.find((step) => step.id === connectionStart.stepId);

        if (sourceStep?.step_type === 'condition') {
          // For condition steps, add a basic condition by default
          onAddConnection(connectionStart.stepId, targetStepId, {
            condition_label: 'Condition',
            condition_config: {
              type: 'custom',
              custom_condition: 'step_reached',
            },
          });
        } else {
          // For non-condition steps, add a regular connection
          onAddConnection(connectionStart.stepId, targetStepId, {
            condition_label: 'Always',
            condition_config: { type: 'always' },
          });
        }

        // Reset connection state
        setIsCreatingConnection(false);
        setConnectionStart(null);
        setConnectionEnd(null);
      }
    },
    [connectionStart, onAddConnection, steps],
  );

  // Function to calculate connection points between steps
  const getConnectionPoints = (fromStep, toStep) => {
    const fromIndex = steps.findIndex(s => s.id === fromStep);
    const toIndex = steps.findIndex(s => s.id === toStep);
    
    console.log('Calculating connection points:', {
      fromStep,
      toStep,
      fromIndex,
      toIndex,
      steps: steps.map(s => ({ id: s.id, name: s.name }))
    });
    
    if (fromIndex === -1 || toIndex === -1) {
      console.warn('Could not find step indices:', { fromStep, toStep });
      return null;
    }

    // Calculate vertical positions based on step indices
    const stepHeight = 120; // Approximate height of a step node
    const connectorHeight = 40; // Height of the connector between steps
    const stepSpacing = stepHeight + connectorHeight;
    
    // Calculate Y positions
    const fromY = fromIndex * stepSpacing + stepHeight;
    const toY = toIndex * stepSpacing;
    
    const points = {
      startNode: { x: 400, y: fromY },
      endNode: { x: 400, y: toY }
    };

    console.log('Calculated connection points:', points);
    return points;
  };

  // Add a global mouse move event listener for tracking the connection
  useEffect(() => {
    if (!isCreatingConnection) return;

    const handleGlobalMouseMove = (e) => {
      if (!isCreatingConnection || !canvasRef.current) return;

      const canvasRect = canvasRef.current.getBoundingClientRect();

      // Calculate position relative to canvas, accounting for zoom and pan
      const x = (e.clientX - canvasRect.left - pan.x) / zoom;
      const y = (e.clientY - canvasRect.top - pan.y) / zoom;

      setConnectionEnd({ x, y });
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
    };
  }, [isCreatingConnection, zoom, pan]);

  // Add a global mouse up event listener to cancel connection creation
  useEffect(() => {
    if (!isCreatingConnection) return;

    const handleGlobalMouseUp = () => {
      setIsCreatingConnection(false);
      setConnectionStart(null);
      setConnectionEnd(null);
    };

    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isCreatingConnection]);

  const [{ isOver }, drop] = useDrop(() => ({
    accept: 'JOURNEY_STEP',
    drop: (item, monitor) => {
      const offset = monitor.getClientOffset();
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const x = offset.x - canvasRect.left;
      const y = offset.y - canvasRect.top;
      
      onAddStep(item.type, { x, y });
      return { dropped: true };
    },
    collect: (monitor) => ({
      isOver: !!monitor.isOver(),
    }),
  }));

  // Calculate bounds for draggable nodes
  const getNodeBounds = () => {
    const padding = 20; // Padding from edges
    return {
      left: padding,
      top: padding,
      right: canvasBounds.width - 180 - padding, // 180 is approximate node width
      bottom: canvasBounds.height - 100 - padding, // 100 is approximate node height
    };
  };

  // Ensure step positions are within bounds
  const ensureStepInBounds = (step) => {
    const bounds = getNodeBounds();
    const x = Math.max(bounds.left, Math.min(bounds.right, step.config?.x || 100));
    const y = Math.max(bounds.top, Math.min(bounds.bottom, step.config?.y || 100));
    
    if (x !== step.config?.x || y !== step.config?.y) {
      onUpdateStep(step.id, {
        ...step,
        config: { ...step.config, x, y },
      });
    }
    
    return { x, y };
  };

  // Calculate total height needed for all steps and connectors
  const totalHeight = steps.length * (120 + 40); // stepHeight + connectorHeight

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', position: 'relative', minHeight: totalHeight }}>
      {/* SVG container for connection lines */}
      <svg
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1,
          overflow: 'visible'
        }}
        viewBox={`0 0 800 ${totalHeight}`}
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <marker
            id="arrowhead"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill="#666" />
          </marker>
          <style>
            {`
              .connection-path {
                stroke-linecap: round;
                stroke-linejoin: round;
              }
              .connection-flow {
                animation: flowAnimation 30s linear infinite;
              }
              @keyframes flowAnimation {
                0% { stroke-dashoffset: 1000; }
                100% { stroke-dashoffset: 0; }
              }
            `}
          </style>
        </defs>
        {connections.map((connection) => {
          console.log('Rendering connection:', connection);
          
          const points = getConnectionPoints(connection.from_step, connection.to_step);
          if (!points) {
            console.warn('No points calculated for connection:', connection);
            return null;
          }

          console.log('Rendering ConnectionLine with points:', {
            connectionId: connection.id,
            points,
            label: connection.condition_label,
            isSelected: selectedStep?.id === connection.from_step
          });

          return (
            <ConnectionLine
              key={connection.id}
              startNode={points.startNode}
              endNode={points.endNode}
              label={connection.condition_label}
              isSelected={selectedStep?.id === connection.from_step}
              onClick={() => onSelectStep(steps.find(s => s.id === connection.from_step))}
            />
          );
        })}
      </svg>

      {/* Steps container */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {steps.length === 0 ? (
          <Box 
            sx={{ 
              textAlign: 'center', 
              py: 8,
              border: '2px dashed',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Typography variant="h6" color="textSecondary" gutterBottom>
              No steps yet
            </Typography>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Drag a step from the palette or click the button below to start building your journey
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => onAddStep()}
              sx={{ mt: 2 }}
            >
              Add Step
            </Button>
          </Box>
        ) : (
          steps.map((step, index) => {
            // console.log('Rendering step:', {
            //   stepId: step.id,
            //   stepName: step.name,
            //   index,
            //   isSelected: selectedStep?.id === step.id
            // });
            
            return (
              <React.Fragment key={step.id}>
                <StepNode
                  step={step}
                  isSelected={selectedStep?.id === step.id}
                  onClick={() => onSelectStep(step)}
                  onDelete={() => onDeleteStep(step.id)}
                  onConnectTo={onAddConnection}
                  onEditConnection={onEditConnection}
                  onDeleteConnection={onDeleteConnection}
                  steps={steps}
                  connections={connections.filter(c => c.from_step === step.id)}
                  isEntryPoint={step.is_entry_point}
                  isGoal={index === steps.length - 1}
                  isConnecting={isConnecting}
                  isConnectionSource={connectionSource === step.id}
                  onStartConnection={onStartConnection}
                  onCompleteConnection={onCompleteConnection}
                  journey={journey}
                />
                {index < steps.length - 1 && (
                  <Box 
                    sx={{ 
                      height: 40, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      position: 'relative',
                    }}
                  >
                    <Box 
                      sx={{ 
                        width: 2, 
                        height: '100%', 
                        bgcolor: 'divider',
                        position: 'absolute',
                      }} 
                    />
                    <Box 
                      sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.main',
                        position: 'absolute',
                      }} 
                    />
                  </Box>
                )}
              </React.Fragment>
            );
          })
        )}
      </Box>
    </Box>
  );
};

export default JourneyCanvas; 